import React, { useState } from "react";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { ScrollArea } from "@shared/ui/scroll-area";
import { Plus } from "lucide-react";
import { usePlaceholderGroups, usePlaceholdersByGroup } from "../../hooks";

interface PlaceholderSelectorProps {
  onSelectPlaceholder: (placeholder: string) => void;
}

export const PlaceholderSelector: React.FC<PlaceholderSelectorProps> = ({
  onSelectPlaceholder,
}) => {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: groupNames = [], isLoading: loadingGroups } =
    usePlaceholderGroups();

  const handleGroupClick = (groupName: string) => {
    setSelectedGroup(groupName);
    setSearchQuery(""); // optionally clear search on group change
  };

  const { data: placeholders = [], isLoading: loadingPlaceholders } =
    usePlaceholdersByGroup(selectedGroup);

  const filteredPlaceholders = placeholders.filter((p) =>
    p.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8">
          <Plus className="h-4 w-4 mr-1" />
          Insert Placeholder
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[520px] p-0" align="start">
        <div className="grid grid-cols-[200px_1fr] divide-x">
          <ScrollArea className="h-[400px]">
            <div className="p-4 space-y-2">
              {loadingGroups ? (
                <div className="text-sm">Loading...</div>
              ) : (
                groupNames.map((group) => (
                  <Button
                    key={group}
                    variant={selectedGroup === group ? "secondary" : "ghost"}
                    className="w-full justify-start font-normal"
                    onClick={() => handleGroupClick(group)}
                  >
                    {group}
                  </Button>
                ))
              )}
            </div>
          </ScrollArea>
          <div className="flex flex-col">
            <div className="p-4 border-b">
              <Input
                placeholder="Search placeholders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8"
              />
            </div>
            <ScrollArea className="h-[344px]">
              <div className="p-4 space-y-2">
                {loadingPlaceholders ? (
                  <div className="text-sm">Loading...</div>
                ) : (
                  filteredPlaceholders.map((placeholder) => (
                    <Button
                      key={placeholder}
                      variant="ghost"
                      className="w-full justify-start font-mono text-sm"
                      onClick={() => onSelectPlaceholder(`{${placeholder}}`)}
                    >
                      {placeholder}
                    </Button>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
